<!-- @format -->
<template>
    <div class="phone-number-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-show="currentStep === steps.loading" class="step">
                    <loading-screen-svg />
                </div>

                <div v-show="currentStep === steps.add" class="step add">
                    <voip-svg />
                    <h2 class="title">Add Your Phone Number</h2>
                    <div class="bordered-section-container">
                        <span class="bordered-section-header">Benefits</span>
                        <ul class="benefits-container">
                            <li>
                                <k24-row-checkmark-svg color="#7E5DFF" />
                                <div>Send calls directly from the GlassHive platform.</div>
                            </li>
                            <li>
                                <k24-row-checkmark-svg color="#7E5DFF" />
                                <div>
                                    Capture call data, including duration, timestamps, and call
                                    notes, all linked to client records within GlassHive for better
                                    visibility.
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div class="bordered-section-container">
                        <span class="bordered-section-header">Details</span>

                        <ul class="details-container">
                            <li>
                                Add your phone number to enable outbound calls. When you register
                                with GlassHive, your number remains connected to your carrier.
                                However, due to the verified caller ID policies set by various
                                carriers, your number may be blocked.
                            </li>
                            <li>Disclaimers about the integration.</li>
                            <li>
                                Colonies rich in heavy atoms two ghostly white figures in coveralls
                                and helmets are little good evidence encyclopaedia galactica from
                                which we spring?
                            </li>
                        </ul>
                    </div>
                </div>

                <div v-show="currentStep === steps.setup" class="step setup">
                    <div class="description">
                        <h2 class="title">Add Your Phone Number</h2>
                        <p>
                            To verify your phone number, you’ll receive a code via SMS message or
                            phone call.
                        </p>
                    </div>
                    <div class="form-container">
                        <div class="phone-name-container">
                            <app-input
                                v-model="phoneNumberName"
                                type="text"
                                label="Phone Number Name"
                                placeholder="Assign a name to this number to make it easier to identify"
                                description="Optional: this name will be displayed alongside your phone number."
                                required
                            />
                        </div>
                        <div class="phone-container">
                            <label :class="{ 'has-error': !isValid && !!phoneNumberError }"
                                >Phone Number <span class="required">*</span></label
                            >
                            <phone-country-selector
                                v-model="phoneNumber"
                                :valid="isValid"
                                :error="!isValid && !!phoneNumberError"
                                clearable
                                default-country-code="US"
                                @update="onUpdate"
                            />
                            <div v-if="phoneNumberError" class="has-error">
                                {{ phoneNumberError }}
                            </div>
                        </div>
                        <div
                            v-if="isWaitingForVerificationCode"
                            class="verification-code-container"
                        >
                            <label :class="{ 'has-error': !isValid && !!verificationCodeError }"
                                >Verification Code <span class="required">*</span></label
                            >
                            <app-input
                                v-model="verificationCode"
                                :disabled="!phoneNumber"
                                description="We've sent a verification code to your provided phone number. It may take a few minutes to get there."
                                :is-valid="!verificationCodeError"
                                :error="verificationCodeError"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget">Cancel</app-button>
                </div>
                <div class="action-container">
                    <app-button
                        v-if="currentStep === steps.add"
                        :loading="currentStep.loading"
                        @click="currentStep.action"
                    >
                        {{ currentStep.buttonText }}
                    </app-button>
                    <div v-else-if="currentStep === steps.setup" class="action-buttons">
                        <app-button
                            v-show="!isWaitingForVerificationCode"
                            :disabled="!canResendCode"
                            class="save-btn"
                            @click="handleSendVerificationCode"
                        >
                            {{ sendVerificationLabel }}
                        </app-button>
                        <app-button
                            v-show="isWaitingForVerificationCode"
                            class="save-btn"
                            size="secondary"
                            :disabled="!canResendCode"
                            @click="handleSendVerificationCode"
                        >
                            {{ resendCodeLabel }}
                        </app-button>
                        <app-button
                            v-show="isWaitingForVerificationCode"
                            class="save-btn"
                            :disabled="!phoneNumber && !isValid"
                            @click="handleVerifyVerificationCode"
                            >Verify
                        </app-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const PhoneCountrySelector = () =>
    import('@/components/phone-country-input/phone-country-selector');
const K24RowCheckmarkSvg = () => import('@/components/svg/grid/k24-row-checkmark-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const VoipSvg = () => import('@/components/svg/voip-svg');

import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'phone-number-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'phone-country-selector': PhoneCountrySelector,
        'k24-row-checkmark-svg': K24RowCheckmarkSvg,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'voip-svg': VoipSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            phoneNumberName: '',
            phoneNumber: '',
            verificationCodeId: null,
            isWaitingForVerificationCode: false,
            formattedNumber: '',
            phoneNumberError: null,
            verificationCode: null,
            verificationCodeError: null,
            isValid: null,
            isLoading: false,

            canResendCode: true,
            timer: null,

            currentStep: null,

            steps: {
                add: {
                    name: 'add',
                    title: 'Setup Calling',
                    buttonText: 'Add Phone Number',
                    loading: false,
                    action: this.goToSetupStep,
                },

                setup: {
                    name: 'setup',
                    title: 'Setup Calling',
                    buttonText: 'Verify',
                    loading: false,
                    action: null,
                },

                loading: { title: 'Loading...', buttonText: 'Loading...', loading: true },
            },
        };
    },

    computed: {
        resendCodeLabel() {
            return this.canResendCode ? 'Resend Code' : `Resend Code in ${this.timer}`;
        },

        sendVerificationLabel() {
            return this.canResendCode
                ? 'Send Verification Code'
                : `Send Verification Code in ${this.timer}`;
        },
    },

    created() {
        this.currentStep = this.steps['loading'];
        this.goToAddStep();
    },

    methods: {
        goToAddStep() {
            this.currentStep = this.steps['add'];
        },

        goToSetupStep() {
            this.currentStep = this.steps['setup'];
        },

        showSuccessModal() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'success-popup',
                    title: 'Calling Setup Complete',
                    content: `<p>Your phone number is successfully set up! You can now make calls, and your number ${this.phoneNumber} will show on the receiving end.</p>`,
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        onUpdate({ formattedNumber, isValid }) {
            if (formattedNumber !== this.formattedNumber) {
                this.phoneNumberError = null;
            }
            this.isValid = isValid;
            this.formattedNumber = formattedNumber;
        },

        async checkIfNumberInUse(phoneNumber) {
            const usedNumbers = ['(469) 431-0097'];

            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(usedNumbers.includes(phoneNumber));
                }, 500);
            });
        },

        async handleSendVerificationCode() {
            this.phoneNumberError = null;
            this.verificationCodeError = null;

            if (!this.phoneNumber || !this.isValid) {
                this.phoneNumberError = 'Please enter a valid phone number.';
                return;
            }

            this.isLoading = true;
            try {
                const isNumberInUse = await this.checkIfNumberInUse(this.phoneNumber);
                if (isNumberInUse) {
                    this.isValid = false;
                    this.phoneNumberError = 'This number is already in use.';
                    return;
                }

                this.verificationCodeId = 123456789;
                this.isWaitingForVerificationCode = true;
                this.resendCodeCountdown();
            } catch (error) {
                this.verificationCodeError = 'An unexpected error occurred. Please try again.';
            } finally {
                this.isLoading = false;
            }
        },

        async handleVerifyVerificationCode() {
            this.verificationCodeError = null;
            this.phoneNumberError = null;

            if (!this.phoneNumber || !this.isValid) {
                this.phoneNumberError = 'Please enter a valid phone number.';
                return;
            }

            if (!this.verificationCode) {
                this.verificationCodeError = 'Please enter the verification code.';
                return;
            }

            try {
                this.isLoading = true;

                // Simulate invalid code case
                if (this.verificationCode === '123456789') {
                    this.verificationCodeError = 'Invalid Code';
                    return;
                }

                this.closeWidget();
                this.showSuccessModal();
            } catch (error) {
                this.verificationCodeError = 'An unexpected error occurred. Please try again.';
            } finally {
                this.isLoading = false;
            }
        },

        resendCodeCountdown() {
            const countDownSeconds = 30;
            this.canResendCode = false;
            this.timer = countDownSeconds;
            const duration = this.$moment.duration(countDownSeconds, 's');

            const timerIntervalId = setInterval(() => {
                duration.subtract(1, 's');
                this.timer = duration.seconds();

                if (duration.asMilliseconds() !== 0) {
                    return;
                }

                clearInterval(timerIntervalId);
                this.timer = null;
                this.canResendCode = true;
            }, 1000);
        },
    },
};
</script>

<style>
.phone-number-widget .required,
.phone-number-widget .has-error {
    color: var(--color-text-warning);
}

.phone-number-widget .content-wrapper .step.setup .form-container .phone-container div.has-error {
    font-size: 14px;
    margin-top: 8px;
}

.phone-number-widget .step.add .title {
    font-family: Nunito;
    font-weight: 800;
    font-size: 20px;
    line-height: 25px;
    letter-spacing: 0%;
}

.phone-number-widget .step.add .voip-svg {
    width: 100%;
    margin-bottom: 16px;
}

.phone-number-widget .step.add .title {
    text-align: center;
    margin-bottom: 32px;
}

.phone-number-widget .step.setup .title {
    margin-bottom: 8px;
}

.phone-number-widget .step.setup .description,
.phone-number-widget .step.setup .phone-container {
    margin-bottom: 24px;
}

.phone-number-widget .step.add .benefits-container li {
    display: flex;
    align-items: center;
}

.phone-number-widget .step.add .benefits-container li:first-of-type {
    margin-bottom: 16px;
}

.phone-number-widget .step.add .k24-row-checkmark-svg {
    margin-right: 24px;
    min-height: 16px;
    min-width: 16px;
}

.phone-number-widget .step.add .k24-row-checkmark-svg path {
    min-height: 16px;
    min-width: 16px;
}

.phone-number-widget .bordered-section-container {
    border: 1px solid var(--color-border-accent-silvergray);
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    padding: 30px 24px 24px;
    margin-bottom: 32px;
}

.phone-number-widget .bordered-section-container:first-child {
    margin-top: 12px;
}

.phone-number-widget .bordered-section-container::before {
    content: '';
    position: absolute;
    top: -1px;
    height: 1px;
    background: var(--color-background-secondary);
}

.phone-number-widget .bordered-section-container::before {
    right: 0;
    left: 0;
    margin: 0 auto;
    width: 98px;
}

.phone-number-widget .details-container li:not(:last-child) {
    margin-bottom: 40px;
}

.phone-number-widget .phone-container {
    margin-bottom: 15px;
}

.phone-number-widget .bordered-section-container span.bordered-section-header {
    position: absolute;
    right: 0;
    left: 0;
    top: 0;
    margin: 0 auto;
    transform: translateY(-50%);
    text-align: center;
    font: normal normal 500 18px/11px 'Roboto';
    letter-spacing: 0px;
    color: var(--color-text-default);
}

.phone-number-widget .bordered-section-container .k74-checkmark-svg {
    font-family: Roboto;
    font-weight: 400;
    font-size: 16px;
    line-height: 25px;
    letter-spacing: 0%;
}

.phone-number-widget .action-container,
.phone-number-widget .action-buttons {
    display: flex;
    gap: 19px;
    align-items: center;
}

.phone-number-widget .action-container .secondary-btn {
    border: 2px solid var(--color-border-button-secondary);
    background: transparent;
    color: var(--color-text-button-secondary);
}

.phone-number-widget .action-container .secondary-btn:hover {
    background: var(--color-background-button-secondary-hover);
    color: var(--color-text-button-secondary-hover);
}

.phone-number-widget .app-button {
    height: 40px;
}

.phone-number-widget .transparent-btn {
    background: transparent;
    color: var(--color-text-subtle);
}

.phone-number-widget .widget-contents-container .transparent-btn:hover {
    background: var(--color-background-button-alternate-hover);
}
</style>
